import React, { useState, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { setPromoShopItemDisabled, updatePromoShopItem } from '../../../apis/atlantis.api';
import ImageUploadField from '../../shared-components/ImageUploadField';
import { VipPromoKind } from '../../../utils/constants';
import {
  getRankOptions,
  normalizeOptionalRank,
  validateLeaderboardRanks,
} from '../../../utils/promoRankValidation';

const ShopItemDetailsForm = ({
  shopItem,
  onChange,
  token,
  handleClose,
  isVip = false,
  isWaitlist = false,
  promo,
  shopItems = [],
  refreshShopItems,
  enableOnly = false,
  readOnly = false,
}) => {
  const fieldsLocked = enableOnly || readOnly;
  const [formValues, setFormValues] = useState(shopItem);
  const [loading, setLoading] = useState(false);
  const [rankError, setRankError] = useState('');
  const granted = isVip || isWaitlist;

  const hasExistingRank =
    shopItem.rank !== undefined && shopItem.rank !== null && shopItem.rank !== '';
  const isLeaderboardPromo = promo?.vipPromoKind === VipPromoKind.LEADERBOARD;
  const showRank = isLeaderboardPromo || hasExistingRank;
  const rankOptions = getRankOptions(promo?.rankN ?? shopItems.length, formValues.rank);

  useEffect(() => {
    setFormValues(shopItem);
    setRankError('');
  }, [shopItem]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
    onChange({ ...formValues, [name]: value });
    if (name === 'rank') {
      setRankError('');
    }
  };

  const handleSubmit = async formValues => {
    if (readOnly) return;
    if (enableOnly) {
      setLoading(true);
      try {
        await setPromoShopItemDisabled(
          shopItem.promoId,
          shopItem.itemId,
          formValues.disabled === true,
          token
        );
        await refreshShopItems?.();
        handleClose();
      } catch (error) {
        console.error('Failed to update shop item status:', error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Leaderboard prize items must keep a rank — the rank-fit grant pays by
    // item.rank, so clearing it makes the prize unwinnable (Gwen 2026-07-07).
    if (isLeaderboardPromo && normalizeOptionalRank(formValues.rank) === null) {
      setRankError('Leaderboard items must have a rank — pick a place (1..N).');
      return;
    }

    const candidateItems = shopItems.map(item =>
      item.itemId === shopItem.itemId ? { ...item, rank: formValues.rank } : item
    );
    const validationError = validateLeaderboardRanks(candidateItems, promo?.vipPromoKind);

    if (validationError) {
      setRankError(validationError);
      return;
    }

    setLoading(true);
    // VIP reward items are free — cost/ogCost are locked to 0 here too, matching
    // the create form, so the edit path can't reintroduce a nonzero cost.
    const itemDetails = {
      cost: granted ? 0 : formValues.cost,
      currency: shopItem.currency,
      description: formValues.description,
      imageUrl: formValues.imageUrl,
      name: formValues.name,
      ogCost: granted ? 0 : formValues.ogCost,
      disabled: formValues.disabled,
    };
    if (showRank) {
      itemDetails.rank = normalizeOptionalRank(formValues.rank);
    }

    setLoading(true);
    try {
      await updatePromoShopItem(shopItem.promoId, shopItem.itemId, itemDetails, token);
      await refreshShopItems?.();
      handleClose();
    } catch (error) {
      console.error('Failed to update shop item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render form fields
  const renderFormField = (id, label, type = 'text', disabled = false) => (
    <div className="mb-4">
      <label
        htmlFor={id}
        className="form-label text-muted fw-medium mb-2"
        style={{ fontSize: '0.875rem' }}
      >
        {label}
      </label>
      <input
        type={type}
        className="form-control form-control-lg shadow-none border-light-subtle rounded-3"
        id={id}
        name={id}
        value={formValues[id] || ''}
        onChange={handleChange}
        disabled={disabled}
        style={{
          padding: '0.75rem 1rem',
          fontSize: '1rem',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        }}
      />
    </div>
  );

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit(formValues);
      }}
      className="bg-white p-4 rounded-4 shadow-sm"
    >
      <h5 className="mb-4 fw-semibold text-dark">
        Item Details
        {fieldsLocked && (
          <span className="badge bg-secondary-subtle text-secondary ms-2 rounded-pill">
            {readOnly ? 'Read only' : 'Other fields are read-only'}
          </span>
        )}
      </h5>

      <div className="row g-4">
        <div className="col-md-6">
          {renderFormField('name', 'Name', 'text', fieldsLocked)}

          <div className="mb-4">
            <label
              htmlFor="description"
              className="form-label text-muted fw-medium mb-2"
              style={{ fontSize: '0.875rem' }}
            >
              Description
            </label>
            <textarea
              className="form-control shadow-none border-light-subtle rounded-3"
              id="description"
              name="description"
              value={formValues.description || ''}
              onChange={handleChange}
              rows="4"
              disabled={fieldsLocked}
              style={{
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
            />
          </div>

          {granted ? (
            <div className="mb-4">
              <label
                className="form-label text-muted fw-medium mb-2"
                style={{ fontSize: '0.875rem' }}
              >
                Cost
              </label>
              <input
                type="number"
                className="form-control form-control-lg shadow-none border-light-subtle rounded-3"
                value={0}
                disabled
                style={{ padding: '0.75rem 1rem', fontSize: '1rem' }}
              />
              <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                Granted rewards are free. Cost is always 0.
              </small>
            </div>
          ) : (
            <div className="row g-3">
              <div className="col-md-6">
                {renderFormField('ogCost', 'Original Cost', 'number', fieldsLocked)}
              </div>
              <div className="col-md-6">
                {renderFormField('cost', 'Discount Cost', 'number', fieldsLocked)}
              </div>
            </div>
          )}

          {showRank && (
            <div className="mb-4">
              <label
                htmlFor="rank"
                className="form-label text-muted fw-medium mb-2"
                style={{ fontSize: '0.875rem' }}
              >
                Leaderboard Rank
              </label>
              <Form.Select
                id="rank"
                name="rank"
                value={formValues.rank ?? ''}
                onChange={handleChange}
                disabled={fieldsLocked}
                className="form-control-lg shadow-none border-light-subtle rounded-3"
              >
                {!isLeaderboardPromo && <option value="">No leaderboard rank</option>}
                {rankOptions.map(rank => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </Form.Select>
              <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
                {isLeaderboardPromo
                  ? 'Required. Ranks must be distinct and contiguous from 1 (1 = 🥇).'
                  : 'Optional. Ranked items must be distinct and contiguous from 1.'}
              </small>
              {rankError && <div className="text-danger mt-2">{rankError}</div>}
            </div>
          )}

          {isWaitlist ? (
            <div className="mb-4">
              <label
                className="form-label text-muted fw-medium mb-2"
                style={{ fontSize: '0.875rem' }}
              >
                Item Behavior
              </label>
              <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                Granted prize box with rewards — winners open it later.
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <label
                htmlFor="ungiftable"
                className="form-label text-muted fw-medium d-flex align-items-center mb-2"
                style={{ fontSize: '0.875rem' }}
              >
                Direct Item (No Rewards)
                <span
                  className="badge bg-success-subtle text-success ms-2 rounded-pill"
                  style={{ fontSize: '0.7rem' }}
                >
                  Read Only
                </span>
              </label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="ungiftable"
                  id="ungiftable"
                  disabled={true}
                  checked={shopItem.ungiftable === true}
                  style={{ width: '2.5em', height: '1.25em' }}
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="disabled"
              className="form-label text-muted fw-medium d-flex align-items-center mb-2"
              style={{ fontSize: '0.875rem' }}
            >
              Item Status
            </label>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                name="disabled"
                id="disabled"
                aria-label="Enable or disable item"
                disabled={readOnly}
                checked={formValues.disabled === true}
                onChange={e => {
                  const newValues = { ...formValues, disabled: e.target.checked };
                  setFormValues(newValues);
                  onChange(newValues);
                }}
                style={{ width: '2.5em', height: '1.25em' }}
              />
              <label className="form-check-label" htmlFor="disabled">
                &nbsp;Disabled (Not Purchasable)
              </label>
            </div>
            <small className="form-text text-muted" style={{ fontSize: '0.75rem' }}>
              Toggle ON to prevent users from purchasing this item. Toggle OFF to make item
              available.
            </small>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Item Image</h6>
              {fieldsLocked ? (
                <img
                  src={formValues.imageUrl}
                  alt={formValues.name || 'Item image'}
                  style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain' }}
                />
              ) : (
                <ImageUploadField
                  value={formValues.imageUrl}
                  onChange={url => {
                    const newValues = { ...formValues, imageUrl: url };
                    setFormValues(newValues);
                    onChange(newValues);
                  }}
                  token={token}
                  id="edit-shop-item-image"
                />
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-medium">Inventory Settings</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  {renderFormField('upperLimit', 'Inventory', 'text', true)}
                </div>
                <div className="col-md-6">
                  {renderFormField('dailyPurchaseLimit', 'Daily Purchase Limit', 'text', true)}
                </div>
              </div>
              <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Inventory settings can only be modified during item creation.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-light rounded-3 px-4 py-2"
          onClick={handleClose}
          style={{ fontSize: '0.95rem' }}
        >
          Cancel
        </button>
        {!readOnly && (
          <button
            type="submit"
            className="btn btn-primary rounded-3 px-4 py-2 ms-3"
            disabled={loading}
            style={{
              fontSize: '0.95rem',
              backgroundColor: '#6366f1',
              borderColor: '#6366f1',
              boxShadow: '0 2px 4px rgba(99, 102, 241, 0.25)',
            }}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" role="status" className="me-2" />
                <span>Saving...</span>
              </>
            ) : enableOnly ? (
              'Save status'
            ) : (
              'Save Changes'
            )}
          </button>
        )}
      </div>
    </form>
  );
};

export default ShopItemDetailsForm;
